import {
  Box,
  Button,
  DialogTitle,
  DialogActions,
  IconButton,
  FormControl,
  SelectChangeEvent,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  FormHelperText,
  Autocomplete,
  DialogContent,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { ChangeEvent, useState, useEffect } from "react";
import { Close, DoorSliding } from "@mui/icons-material";
import { TimePicker } from "../../../components/shared/TimePicker";
import { useDriversUsage } from "../../../hooks/react-query/useDrivers";
import {
  useEquipmentUsageBySicId,
  useGetEquipmentBySic,
} from "../../../hooks/react-query/useEquipments";
import { useDoorsBySicIdAndPlanDate } from "../../../hooks/react-query/useDoors";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";
import { SelectedEquipmentKitType } from "../../../types/equipment.type";
import { routeTrapType } from "../../../constants/RouteTrapTypes";
import {
  MAX_ROUTE_NAME_LENGTH,
  MAX_TRAP_NAME_LENGTH,
} from "../../../constants/fieldNameLengthConstants";
import { usePlanStartDate } from "../../../hooks/usePlanStartDate";
import {
  doorAvailability,
  getDoorOptionsFromDoors,
} from "../shared/doorNumberUtils";
import { AutocompleteAvailabilityOption } from "../../../components/shared/AutocompleteAvailabilityOption";
import { getDriverOptions, driverAvailability } from "../shared/driverUtils";
import {
  getStraightTruckOptions,
  getTractorOptions,
  getTrailerOptions,
  tractorAvailability,
  trailerAvailability,
  straightTruckAvailability,
} from "../shared/equipmentUtils";
import Loading from "../../../components/shared/layout/Loading";
import { planningStatus } from "../../../types/status.type";
import { displayMessage, unavailableText } from "../../../constants/strings";
import { TextField } from "../../../components/shared/layout/TextFieldWithWarning";
import { planType } from "../../../constants/RouteTrapTypes";
import { PlanTableFormat } from "../../../types/planning/plan.type";
import { AutocompleteAvailabilityOptionDriver } from "../../../components/shared/AutocompleteAvailabilityOptionDriver";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import { EquipmentDropdown } from "../../../components/shared/EquipmentDropdown";
import { transformBackendResponse } from "../../../components/shared/equipmentDropdown.utils";
import {
  canRemoveDoor,
  canRemoveTrailerOrStraightTruck,
  getRemovalBlockedMessage,
  shouldShowDoorRemovalError,
  shouldShowTrailerRemovalError,
  shouldShowStraightTruckRemovalError,
} from "./equipmentRemovalValidation";

type EditRouteValidationErrors = {
  presetRoute?: string;
  routeName?: string;
  startTime?: string;
  driver?: string;
  trailerOrStraightTruckEquipment?: string;
  tractorEquipment?: string;
};

export type EditedPlan = Pick<
  PlanTableFormat,
  "id" | "routeEnd" | "name" | "startTime" | "endTime"
> & {
  driverId: number | null;
  doorId: number | null;
  trailerEquipmentId: number | null;
  tractorEquipmentId: number | null;
  straightTruckEquipmentId: number | null;
};

type FormHelperText = Record<keyof EditedPlan, string>;

interface EditRouteConfirmDialogProps {
  plan: PlanTableFormat;
  open: boolean;
  savePlan: (editedPlan: EditedPlan) => void;
  cancelHandler: () => void;
  isLoading?: boolean;
}

function mapEditedPlan(plan: PlanTableFormat): EditedPlan {
  return {
    driverId: plan.driver?.id ?? 0,
    endTime: plan.endTime,
    id: plan.id,
    name: plan.name,
    routeEnd: plan.routeEnd ?? "",
    startTime: plan.startTime,
    doorId: plan.door,
    trailerEquipmentId: plan.trailer?.id ?? null,
    tractorEquipmentId: plan.tractor?.id ?? null,
    straightTruckEquipmentId: plan.straightTruck?.id ?? null,
  };
}
interface ValidatePossiblyInactiveItemArgs {
  /** Original item that was present in the plan */
  originalItem: {
    id: number;
    isAvailable?: boolean;
    isActive?: boolean;
  } | null;
  /** Edited item id selected on the dropdown */
  selectedItemId: number | null;
  /** Name of the item */
  itemName: string;
}
/**
 * Item validation function for items that can be inactive.
 * The validation is only performed over the initial values, as inactive or deleted items are excluded from dropdown options.
 */
function validatePossiblyInactiveItem({
  originalItem,
  selectedItemId,
  itemName,
}: ValidatePossiblyInactiveItemArgs): string {
  // Omit validation if item was not present originally, no record is selected, or the original id is different from the selected item's id
  if (
    !originalItem ||
    selectedItemId === null ||
    originalItem.id !== selectedItemId
  ) {
    return "";
  }

  if (originalItem.isAvailable === false || originalItem.isActive === false) {
    return displayMessage.default.recordInactive(itemName);
  }

  return "";
}

export function getValidationErrors(
  plan: PlanTableFormat,
  editedPlan: EditedPlan
): EditRouteValidationErrors {
  return {
    presetRoute: "",
    routeName: !editedPlan.name ? "Route Name is required" : "",
    startTime:
      !editedPlan.startTime && plan.type === planType.DELIVERY_ROUTE
        ? "Start Time is required"
        : "",
    driver: validatePossiblyInactiveItem({
      originalItem: plan.driver,
      selectedItemId: editedPlan.driverId,
      itemName: "driver",
    }),
    trailerOrStraightTruckEquipment: validatePossiblyInactiveItem({
      originalItem: plan.trailer ?? plan.straightTruck,
      selectedItemId:
        editedPlan.trailerEquipmentId ?? editedPlan.straightTruckEquipmentId,
      itemName: "equipment",
    }),
    tractorEquipment: validatePossiblyInactiveItem({
      originalItem: plan.tractor,
      selectedItemId: editedPlan.tractorEquipmentId,
      itemName: "equipment",
    }),
  };
}

type PlanningStatusArray = Array<keyof typeof planningStatus>;

const blockSelectingAssignedTrailerOrStraightTruckStatuses: string[] = [
  planningStatus.CLDK,
  planningStatus.CLDV,
  planningStatus.TRAP,
  planningStatus.DISP,
] satisfies PlanningStatusArray;

const blockSelectingAssignedTractorStatuses: string[] = [
  planningStatus.CLDK,
  planningStatus.CLDV,
  planningStatus.DISP,
] satisfies PlanningStatusArray;

export const blockSelectingAssignedDoorStatuses: string[] = [
  planningStatus.CLDK,
  planningStatus.CLDV,
  planningStatus.DISP,
] satisfies PlanningStatusArray;

const blockSelectingAssignedDriverStatuses: string[] = [
  planningStatus.DISP,
] satisfies PlanningStatusArray;

export const EditRouteConfirmDialog = ({
  plan,
  open,
  savePlan,
  cancelHandler,
  isLoading = false,
}: EditRouteConfirmDialogProps) => {
  const isRemoveEquipmentOrDoorValidationEnabled = useIsFeatureFlagEnabled(
    "inbound-3855-remove-equipment-or-door-validation-client"
  );
  const isNewDropdownEnabled = useIsFeatureFlagEnabled(
    "operations-5722-listing-equipments-dropdown-client"
  );
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    if (open) {
      setHasAttemptedSubmit(false);
    }
  }, [open]);

  const initialSelectedEquipmentType = (): SelectedEquipmentKitType | null => {
    if (plan.tractor || plan.trailer) {
      return "Trailer";
    } else if (plan.straightTruck) {
      return "Truck";
    } else {
      return null;
    }
  };
  const [selectedEquipmentType, setSelectedEquipmentType] =
    useState<SelectedEquipmentKitType | null>(initialSelectedEquipmentType);
  const [formHelperText, setFormHelperText] = useState<Partial<FormHelperText>>(
    {}
  );

  const [serviceCenter] = useSelectedServiceCenter();
  const { data: doors = [], isLoading: isLoadingDoors } =
    useDoorsBySicIdAndPlanDate(serviceCenter.id, plan.date);
  const doorOptions = getDoorOptionsFromDoors(doors);

  const { data: drivers = [], isLoading: isLoadingDrivers } = useDriversUsage(
    serviceCenter.id,
    plan.date
  );
  const driverOptions = getDriverOptions(drivers);

  const [editedPlan, setEditedPlan] = useState<EditedPlan>(mapEditedPlan(plan));
  const originalPlan: EditedPlan = mapEditedPlan(plan);

  const {
    data: equipments = { tractors: [], trailers: [], straightTrucks: [] },
    isLoading: isLoadingEquipmentsLegacy,
  } = useEquipmentUsageBySicId(
    serviceCenter.id,
    plan.date,
    !isNewDropdownEnabled
  );

  const { data: equipmentBySic, isLoading: isLoadingEquipmentBySic } =
    useGetEquipmentBySic({
      sicId: serviceCenter.id,
      EquipmentType: [1, 2, 3],
      planDate: plan.date,
      enabled: isNewDropdownEnabled,
    });

  const isLoadingEquipments = isNewDropdownEnabled
    ? isLoadingEquipmentBySic
    : isLoadingEquipmentsLegacy;

  const allOptions = equipmentBySic
    ? transformBackendResponse({
        ...equipmentBySic,
        assigned: [...equipmentBySic.assigned, ...equipmentBySic.loadAndHold],
        loadAndHold: [],
      })
    : [];
  const trailerOptionsBySic = allOptions.filter(
    (o) => o.rawData.equipmentType.id === 2
  );
  const tractorOptionsBySic = allOptions.filter(
    (o) => o.rawData.equipmentType.id === 1
  );
  const straightTruckOptionsBySic = allOptions.filter(
    (o) => o.rawData.equipmentType.id === 3
  );

  const handleHelperText = (
    value: string | number,
    fieldName: keyof EditedPlan
  ) => {
    const updateHelperText = () =>
      setFormHelperText((prev) => ({
        ...prev,
        [fieldName]: `Editing this ${routeTrapType[plan.type]} will change ${plan.name} status from ${plan.status} to CLDK`,
      }));

    const clearHelperText = () =>
      setFormHelperText((prev) => ({
        ...prev,
        [fieldName]: "",
      }));

    const fieldNotAvailableForMassageChanged =
      !plan.isAvailableForMassage &&
      value.toString() !== originalPlan[fieldName]?.toString() &&
      fieldName !== "name";

    /**
     * Validate if the field name is different from name this field won't change the route/trap status.
     */
    if (fieldNotAvailableForMassageChanged) {
      updateHelperText();
    } else {
      clearHelperText();
    }
  };

  function handleChange(
    e:
      | SelectChangeEvent<string>
      | SelectChangeEvent<unknown>
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    const { value, name } = e.target;

    setEditedPlan((prev) => ({
      ...prev,
      [name]: value ? (value as string).trimStart() : "",
    }));

    handleHelperText(value as string, name as keyof EditedPlan);
  }

  // If the user selects the active toggle button again, the active toggle button will be deselected
  const handleSelectEquipmentType = (type: SelectedEquipmentKitType | null) => {
    setSelectedEquipmentType(type === selectedEquipmentType ? null : type);
  };

  const validateForm = () => {
    const validationErrors = getValidationErrors(plan, editedPlan);
    return Object.keys(validationErrors).every(
      (key) => !validationErrors[key as keyof EditRouteValidationErrors]
    );
  };

  const planStartDate = usePlanStartDate({
    startTime: editedPlan.startTime?.toISOString() || null,
    planDate: plan.date,
    timeZone: serviceCenter.timeZone,
    // Need to display the same value regardless of the timezone
    // ex. Sic: DAL Time: 8:00-5:00. We want to show it 8AM even if the user is in LA (in LA 1pm UTC is 6am)
    keepLocalTime: false,
  });

  if (
    open &&
    (isLoading || isLoadingEquipments || isLoadingDoors || isLoadingDrivers)
  ) {
    return (
      <Dialog open={open}>
        <DialogContent>
          <DialogTitle>Loading...</DialogTitle>
          <Loading />
        </DialogContent>
      </Dialog>
    );
  }

  const trailerOptions = getTrailerOptions(equipments.trailers, plan.trailer);
  const tractorOptions = getTractorOptions(equipments.tractors, plan.tractor);
  const straightTruckOptions = getStraightTruckOptions(
    equipments.straightTrucks,
    plan.straightTruck
  );

  const validationErrors = getValidationErrors(plan, editedPlan);

  return (
    <Dialog open={open} onClose={cancelHandler} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", padding: "20px 0px 10px 20px" }}>
        {`Edit "${plan.name}" ${
          plan.type === planType.DELIVERY_ROUTE ? "Route" : "Trap"
        }`}
      </DialogTitle>

      <Box position="absolute" top={10} right={10}>
        <IconButton onClick={cancelHandler}>
          <Close />
        </IconButton>
      </Box>
      <Box sx={{ margin: "1px 1px 1px 20px" }}>
        <Typography fontSize={14} fontWeight={600} mb={1}>
          {`${plan.type === planType.DELIVERY_ROUTE ? "Route" : "Trap"} Information`}
        </Typography>
      </Box>
      <Box sx={{ margin: "10px 20px 20px 20px" }}>
        <TextField
          id="outlined-basic"
          name="name"
          value={editedPlan.name}
          variant="outlined"
          onChange={handleChange}
          sx={{ width: "100%" }}
          label={
            plan.type === planType.DELIVERY_ROUTE
              ? "Route Name *"
              : "Trap Name *"
          }
          inputProps={{
            maxLength:
              plan.type === planType.DELIVERY_ROUTE
                ? MAX_ROUTE_NAME_LENGTH
                : MAX_TRAP_NAME_LENGTH,
          }}
          helperText={validationErrors.routeName}
        />
      </Box>
      <Box sx={{ margin: "1px 1px 1px 20px" }}>
        <Typography fontSize={14} fontWeight={600} mb={1}>
          {`${plan.type === planType.DELIVERY_ROUTE ? "Route" : "Trap"} Details`}
        </Typography>
      </Box>
      {plan.type === planType.DELIVERY_ROUTE && (
        <Box
          sx={{ margin: "1px 20px 20px 20px" }}
          display="flex"
          flexDirection="row"
        >
          <TimePicker
            label="Start Time *"
            value={editedPlan.startTime ? planStartDate : null}
            onChange={(val) => {
              const isValidDate = val && val.isValid();
              if (!plan.canEditStartTime) {
                handleHelperText(
                  isValidDate ? val.toDate().toString() : "",
                  "startTime"
                );
              }
              setEditedPlan((prev) => ({
                ...prev,
                startTime: isValidDate ? val.toDate() : null,
              }));
            }}
            slotProps={{
              textField: {
                sx: { width: "100%" },
                helperText: validationErrors.startTime,
                error: !!validationErrors.startTime,
              },
            }}
          />
        </Box>
      )}
      <Box sx={{ margin: "1px 20px 20px 20px" }}>
        <FormControl fullWidth>
          <Autocomplete
            value={
              doorOptions.find((d) => Number(d.value) === editedPlan.doorId) ??
              null
            }
            options={doorOptions}
            onChange={(_, newValue) => {
              setEditedPlan((prev) => ({
                ...prev,
                doorId: newValue?.value ?? null,
              }));
            }}
            disableClearable={
              isRemoveEquipmentOrDoorValidationEnabled &&
              !canRemoveDoor(plan.status, plan.door)
            }
            getOptionLabel={(door) => door.name}
            renderInput={(params) => {
              const doorRemovalBlocked = !canRemoveDoor(plan.status, plan.door);
              const doorRemovalMessage = doorRemovalBlocked
                ? getRemovalBlockedMessage("door", plan.status, plan.type)
                : "";

              return (
                <TextField
                  {...params}
                  placeholder="Select Door"
                  label="Door Assignment"
                  error={shouldShowDoorRemovalError(
                    plan,
                    editedPlan,
                    hasAttemptedSubmit,
                    isRemoveEquipmentOrDoorValidationEnabled
                  )}
                  helperText={
                    shouldShowDoorRemovalError(
                      plan,
                      editedPlan,
                      hasAttemptedSubmit,
                      isRemoveEquipmentOrDoorValidationEnabled
                    )
                      ? doorRemovalMessage
                      : undefined
                  }
                />
              );
            }}
            groupBy={(option) => option.group}
            getOptionDisabled={(option) =>
              blockSelectingAssignedDoorStatuses.includes(plan.status) &&
              option.group === doorAvailability.ASSIGNED
            }
            renderOption={(props, option) => (
              <AutocompleteAvailabilityOption
                key={option.name}
                listItemProps={props}
                name={option.name}
                assignment={option.assignment}
                leftIcon={<DoorSliding />}
              />
            )}
          />
          <FormHelperText>{formHelperText.doorId}</FormHelperText>
        </FormControl>
      </Box>
      {plan.type === planType.DELIVERY_ROUTE && (
        <Box sx={{ margin: "1px 20px 20px 20px" }}>
          <FormControl fullWidth>
            <Autocomplete
              value={
                driverOptions.find(
                  (d) => Number(d.value) === editedPlan.driverId
                ) ?? null
              }
              options={driverOptions}
              onChange={(_, newValue) => {
                setEditedPlan((prev) => ({
                  ...prev,
                  driverId: newValue?.value ?? null,
                }));
              }}
              getOptionLabel={(driver) => `${driver.name} - ${driver.refCode}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Driver"
                  label="Driver"
                  helperText={validationErrors.driver}
                  warning={
                    validationErrors.driver ? undefined : unavailableText
                  }
                />
              )}
              groupBy={(option) => option.group}
              getOptionDisabled={(option) =>
                (blockSelectingAssignedDriverStatuses.includes(plan.status) &&
                  option.group === driverAvailability.ASSIGNED) ||
                option.group === driverAvailability.INACTIVE
              }
              renderOption={(props, option) => (
                <AutocompleteAvailabilityOptionDriver
                  key={`${option.name}-${option.value}`}
                  listItemProps={props}
                  assignment={option.assignment}
                  name={option.name}
                  refCode={option.refCode}
                />
              )}
            />

            <FormHelperText>{formHelperText.driverId}</FormHelperText>
          </FormControl>
        </Box>
      )}
      <Box sx={{ margin: "1px 10px 1px 20px" }}>
        <Typography fontSize={14} fontWeight={600} mb={1}>
          Select an equipment type
        </Typography>
      </Box>
      <Box sx={{ margin: "10px 20px 20px 20px" }}>
        <ToggleButtonGroup
          color="primary"
          value={selectedEquipmentType}
          exclusive
          aria-label="Platform"
          size="small"
        >
          <ToggleButton
            value="Trailer"
            onChange={() => handleSelectEquipmentType("Trailer")}
          >
            Trailer
          </ToggleButton>
          <ToggleButton
            value="Truck"
            onChange={() => handleSelectEquipmentType("Truck")}
          >
            Straight Truck
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <div role="tabpanel" hidden={selectedEquipmentType !== "Trailer"}>
        <Box sx={{ margin: "10px 20px 20px 20px" }}>
          {isNewDropdownEnabled ? (
            <>
              <EquipmentDropdown
                label="Trailer #"
                equipmentTypeName="Trailer"
                options={trailerOptionsBySic}
                showSicCode={false}
                value={
                  trailerOptionsBySic.find(
                    (o) => o.id === editedPlan.trailerEquipmentId
                  ) ?? null
                }
                disableClearable={
                  isRemoveEquipmentOrDoorValidationEnabled &&
                  !canRemoveTrailerOrStraightTruck(
                    plan.status,
                    plan.type,
                    plan.trailer?.id ?? null
                  )
                }
                getOptionDisabled={(option) =>
                  blockSelectingAssignedTrailerOrStraightTruckStatuses.includes(
                    plan.status
                  ) && !option.isAvailable
                }
                error={shouldShowTrailerRemovalError(
                  plan,
                  editedPlan,
                  hasAttemptedSubmit,
                  isRemoveEquipmentOrDoorValidationEnabled
                )}
                helperText={
                  shouldShowTrailerRemovalError(
                    plan,
                    editedPlan,
                    hasAttemptedSubmit,
                    isRemoveEquipmentOrDoorValidationEnabled
                  )
                    ? getRemovalBlockedMessage(
                        "trailer",
                        plan.status,
                        plan.type
                      )
                    : formHelperText.trailerEquipmentId ??
                      validationErrors.trailerOrStraightTruckEquipment
                }
                onChange={(option) =>
                  setEditedPlan((prev) => ({
                    ...prev,
                    trailerEquipmentId: option ? option.id : null,
                  }))
                }
              />
              &nbsp;
              {plan.type === planType.DELIVERY_ROUTE && (
                <EquipmentDropdown
                  label="Tractor #"
                  equipmentTypeName="Tractor"
                  options={tractorOptionsBySic}
                  showSicCode={false}
                  value={
                    tractorOptionsBySic.find(
                      (o) => o.id === editedPlan.tractorEquipmentId
                    ) ?? null
                  }
                  getOptionDisabled={(option) =>
                    blockSelectingAssignedTractorStatuses.includes(
                      plan.status
                    ) && !option.isAvailable
                  }
                  helperText={
                    formHelperText.tractorEquipmentId ??
                    validationErrors.tractorEquipment
                  }
                  onChange={(option) =>
                    setEditedPlan((prev) => ({
                      ...prev,
                      tractorEquipmentId: option ? option.id : null,
                    }))
                  }
                />
              )}
            </>
          ) : (
            <>
              <FormControl sx={{ width: "100%" }} size="small">
                <Autocomplete
                  value={
                    trailerOptions.find(
                      (d) => Number(d.value) === editedPlan.trailerEquipmentId
                    ) ?? null
                  }
                  options={trailerOptions}
                  onChange={(_, newValue) => {
                    setEditedPlan((prev) => ({
                      ...prev,
                      trailerEquipmentId: newValue?.value ?? null,
                    }));
                  }}
                  disableClearable={
                    isRemoveEquipmentOrDoorValidationEnabled &&
                    !canRemoveTrailerOrStraightTruck(
                      plan.status,
                      plan.type,
                      plan.trailer?.id ?? null
                    )
                  }
                  getOptionLabel={(trailer) => trailer.name}
                  renderInput={(params) => {
                    const trailerRemovalBlocked =
                      !canRemoveTrailerOrStraightTruck(
                        plan.status,
                        plan.type,
                        plan.trailer?.id ?? null
                      );
                    const trailerRemovalMessage = trailerRemovalBlocked
                      ? getRemovalBlockedMessage(
                          "trailer",
                          plan.status,
                          plan.type
                        )
                      : "";

                    const showTrailerError = shouldShowTrailerRemovalError(
                      plan,
                      editedPlan,
                      hasAttemptedSubmit,
                      isRemoveEquipmentOrDoorValidationEnabled
                    );

                    return (
                      <TextField
                        {...params}
                        label="Trailer #"
                        error={showTrailerError}
                        helperText={
                          showTrailerError
                            ? trailerRemovalMessage
                            : validationErrors.trailerOrStraightTruckEquipment
                        }
                        warning={showTrailerError ? undefined : unavailableText}
                      />
                    );
                  }}
                  groupBy={(option) => option.group}
                  getOptionDisabled={(option) =>
                    (blockSelectingAssignedTrailerOrStraightTruckStatuses.includes(
                      plan.status
                    ) &&
                      option.group === trailerAvailability.ASSIGNED) ||
                    option.group === trailerAvailability.INACTIVE
                  }
                  renderOption={(props, option) => (
                    <AutocompleteAvailabilityOption
                      key={option.name}
                      listItemProps={props}
                      name={option.name}
                      assignment={option.assignment}
                    />
                  )}
                />
                <FormHelperText>
                  {formHelperText.trailerEquipmentId}
                </FormHelperText>
              </FormControl>
              &nbsp;
              {plan.type === planType.DELIVERY_ROUTE && (
                <FormControl sx={{ width: "100%" }} size="small">
                  <Autocomplete
                    value={
                      tractorOptions.find(
                        (d) => Number(d.value) === editedPlan.tractorEquipmentId
                      ) ?? null
                    }
                    options={tractorOptions}
                    onChange={(_, newValue) => {
                      setEditedPlan((prev) => ({
                        ...prev,
                        tractorEquipmentId: newValue?.value ?? null,
                      }));
                    }}
                    getOptionLabel={(tractor) => tractor.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tractor #"
                        helperText={validationErrors.tractorEquipment}
                        warning={
                          validationErrors.tractorEquipment
                            ? undefined
                            : unavailableText
                        }
                      />
                    )}
                    groupBy={(option) => option.group}
                    getOptionDisabled={(option) =>
                      (blockSelectingAssignedTractorStatuses.includes(
                        plan.status
                      ) &&
                        option.group === tractorAvailability.ASSIGNED) ||
                      option.group === tractorAvailability.INACTIVE
                    }
                    renderOption={(props, option) => (
                      <AutocompleteAvailabilityOption
                        key={option.name}
                        listItemProps={props}
                        name={option.name}
                        assignment={option.assignment}
                      />
                    )}
                  />
                  <FormHelperText>
                    {formHelperText.tractorEquipmentId}
                  </FormHelperText>
                </FormControl>
              )}
            </>
          )}
        </Box>
      </div>
      <div role="tabpanel" hidden={selectedEquipmentType !== "Truck"}>
        <Box sx={{ margin: "10px 20px 20px 20px" }}>
          {isNewDropdownEnabled ? (
            <EquipmentDropdown
              label="ST #"
              equipmentTypeName="Straight Truck"
              options={straightTruckOptionsBySic}
              showSicCode={false}
              value={
                straightTruckOptionsBySic.find(
                  (o) => o.id === editedPlan.straightTruckEquipmentId
                ) ?? null
              }
              disableClearable={
                isRemoveEquipmentOrDoorValidationEnabled &&
                !canRemoveTrailerOrStraightTruck(
                  plan.status,
                  plan.type,
                  plan.straightTruck?.id ?? null
                )
              }
              getOptionDisabled={(option) =>
                blockSelectingAssignedTrailerOrStraightTruckStatuses.includes(
                  plan.status
                ) && !option.isAvailable
              }
              error={shouldShowStraightTruckRemovalError(
                plan,
                editedPlan,
                hasAttemptedSubmit,
                isRemoveEquipmentOrDoorValidationEnabled
              )}
              helperText={
                shouldShowStraightTruckRemovalError(
                  plan,
                  editedPlan,
                  hasAttemptedSubmit,
                  isRemoveEquipmentOrDoorValidationEnabled
                )
                  ? getRemovalBlockedMessage(
                      "straightTruck",
                      plan.status,
                      plan.type
                    )
                  : formHelperText.straightTruckEquipmentId ??
                    validationErrors.trailerOrStraightTruckEquipment
              }
              onChange={(option) =>
                setEditedPlan((prev) => ({
                  ...prev,
                  straightTruckEquipmentId: option ? option.id : null,
                }))
              }
            />
          ) : (
            <FormControl sx={{ width: "100%" }} size="small">
              <Autocomplete
                value={
                  straightTruckOptions.find(
                    (d) =>
                      Number(d.value) === editedPlan.straightTruckEquipmentId
                  ) ?? null
                }
                options={straightTruckOptions}
                onChange={(_, newValue) => {
                  setEditedPlan((prev) => ({
                    ...prev,
                    straightTruckEquipmentId: newValue?.value ?? null,
                  }));
                }}
                disableClearable={
                  isRemoveEquipmentOrDoorValidationEnabled &&
                  !canRemoveTrailerOrStraightTruck(
                    plan.status,
                    plan.type,
                    plan.straightTruck?.id ?? null
                  )
                }
                getOptionLabel={(straightTruck) => straightTruck.name}
                renderInput={(params) => {
                  const straightTruckRemovalBlocked =
                    !canRemoveTrailerOrStraightTruck(
                      plan.status,
                      plan.type,
                      plan.straightTruck?.id ?? null
                    );
                  const straightTruckRemovalMessage =
                    straightTruckRemovalBlocked
                      ? getRemovalBlockedMessage(
                          "straightTruck",
                          plan.status,
                          plan.type
                        )
                      : "";

                  const showStraightTruckError =
                    shouldShowStraightTruckRemovalError(
                      plan,
                      editedPlan,
                      hasAttemptedSubmit,
                      isRemoveEquipmentOrDoorValidationEnabled
                    );

                  return (
                    <TextField
                      {...params}
                      label="ST #"
                      error={showStraightTruckError}
                      helperText={
                        showStraightTruckError
                          ? straightTruckRemovalMessage
                          : validationErrors.trailerOrStraightTruckEquipment
                      }
                      warning={
                        showStraightTruckError ? undefined : unavailableText
                      }
                    />
                  );
                }}
                groupBy={(option) => option.group}
                getOptionDisabled={(option) =>
                  (blockSelectingAssignedTrailerOrStraightTruckStatuses.includes(
                    plan.status
                  ) &&
                    option.group === straightTruckAvailability.ASSIGNED) ||
                  option.group === straightTruckAvailability.INACTIVE
                }
                renderOption={(props, option) => (
                  <AutocompleteAvailabilityOption
                    key={option.name}
                    listItemProps={props}
                    name={option.name}
                    assignment={option.assignment}
                  />
                )}
              />
              <FormHelperText>
                {formHelperText.straightTruckEquipmentId}
              </FormHelperText>
            </FormControl>
          )}
        </Box>
      </div>
      <DialogActions sx={{ margin: "5px 20px 20px 20px" }}>
        <Button onClick={cancelHandler} variant="text">
          Cancel
        </Button>
        <Button
          onClick={() => {
            // Set attempted submit flag
            setHasAttemptedSubmit(true);

            const payloadToSave: EditedPlan =
              selectedEquipmentType === null
                ? {
                    ...editedPlan,
                    straightTruckEquipmentId: null,
                    tractorEquipmentId: null,
                    trailerEquipmentId: null,
                  }
                : selectedEquipmentType === "Truck"
                  ? {
                      ...editedPlan,
                      tractorEquipmentId: null,
                      trailerEquipmentId: null,
                    }
                  : {
                      ...editedPlan,
                      straightTruckEquipmentId: null,
                    };

            // If feature flag is on, validate removal restrictions against the actual payload
            if (isRemoveEquipmentOrDoorValidationEnabled) {
              const isDoorRemovalBlocked = !canRemoveDoor(
                plan.status,
                plan.door
              );
              const isTrailerRemovalBlocked = !canRemoveTrailerOrStraightTruck(
                plan.status,
                plan.type,
                plan.trailer?.id ?? null
              );
              const isStraightTruckRemovalBlocked =
                !canRemoveTrailerOrStraightTruck(
                  plan.status,
                  plan.type,
                  plan.straightTruck?.id ?? null
                );

              // Check if user is trying to remove blocked equipment (validate against actual payload)
              // For CLDK status: Only block if removing (setting to null), allow changing to different equipment (replace trailer with straight truck or vice versa)
              // For other blocked statuses: Block any change or removal
              const tryingToRemoveDoor =
                isDoorRemovalBlocked &&
                plan.door &&
                (plan.status === planningStatus.CLDK
                  ? payloadToSave.doorId === null
                  : payloadToSave.doorId !== plan.door);
              const tryingToRemoveTrailer =
                isTrailerRemovalBlocked &&
                plan.trailer &&
                (plan.status === planningStatus.CLDK
                  ? payloadToSave.trailerEquipmentId === null &&
                    payloadToSave.straightTruckEquipmentId === null
                  : payloadToSave.trailerEquipmentId !== plan.trailer.id);
              const tryingToRemoveStraightTruck =
                isStraightTruckRemovalBlocked &&
                plan.straightTruck &&
                (plan.status === planningStatus.CLDK
                  ? payloadToSave.straightTruckEquipmentId === null &&
                    payloadToSave.trailerEquipmentId === null
                  : payloadToSave.straightTruckEquipmentId !==
                    plan.straightTruck.id);

              // If any removal is blocked, prevent save
              if (
                tryingToRemoveDoor ||
                tryingToRemoveTrailer ||
                tryingToRemoveStraightTruck
              ) {
                return; // Prevent save, errors will show
              }
            }

            // Validate form and save the same payload we validated
            if (validateForm()) {
              savePlan(payloadToSave);
            }
          }}
          color="secondary"
          variant="contained"
          sx={{ width: "80px" }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
